import {
  forwardRef, useContext, useEffect, useId, useMemo, useRef,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { useField } from 'formik';
import FormGroup from 'react-bootstrap/FormGroup';
import FormCheck from 'react-bootstrap/FormCheck';

import { FormContext, isRequiredField } from './utils';
import type { SimpleCheckboxChangeEvent, SimpleCheckboxInputProps } from './SimpleCheckboxInput';

/**
 * Собственные пропсы Formik-обёртки чекбокса.
 *
 * То, что CheckboxInput достаивает из `{...props}` и обрабатывает сам. Остальные пропсы
 * (`label`, `checked`, `disabled`, `mode`, `triStateLabels`, `hoverable`, ...) наследуются
 * из {@link SimpleCheckboxInputProps} — см. {@link CheckboxInputProps}.
 *
 * В отличие от SelectInput/MultiSelectInput, эта обёртка использует хук `useField`
 * напрямую (без render-prop `<Field>`) и сама достаёт `validationSchema` из FormContext —
 * поэтому внешняя wrapper-компонента не нужна.
 */
export interface CheckboxInputOwnProps {
  /** Ручной признак обязательности; иначе автодетект по validationSchema из FormContext. */
  required?: boolean;
  /**
   * Yup-схема формы.
   *
   * FIXME: в текущей реализации пропс ИГНОРИРУЕТСЯ — обязательность поля определяется по
   * схеме из FormContext (useContext). Тип добавлен для единообразия с SelectInput/
   * MultiSelectInput; подключение пропса — отдельный MR. На runtime не влияет.
   */
  validationSchema?: any;
  /** Колбэк вызывается, когда поле переходит в состояние ошибки валидации. */
  onValidateFail?(error: unknown): void;
}

/**
 * Пропсы Formik-обёртки чекбокса (CheckboxInput).
 *
 * Расширяет {@link SimpleCheckboxInputProps} своими Formik-специфичными полями. `name`
 * сделан обязательным: `useField` без имени бросит runtime-ошибку.
 *
 * `isNextField` (визуальное состояние «следующее обязательное поле») наследуется из
 * SimpleCheckboxInputProps и подключается здесь же — подсветка `.field-is-next` на обёртке
 * FormGroup, гасится при `hasError` или `disabled` (см. CONTEXT.md → «Визуальные состояния поля»).
 */
export type CheckboxInputProps = SimpleCheckboxInputProps & CheckboxInputOwnProps & {
  /** Имя поля формы (Formik). Обязательно: useField без name бросит runtime-ошибку. */
  name: string;
};

const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>((props, ref) => {
  // Передаём в useField только name: для регистрации поля formik'у нужно имя, остальные
  // пропсы (mode, triStateLabels, onChange с нашей сигнатурой SimpleCheckboxChangeEvent,
  // label, ...) он не читает. Передача всего `props` в .tsx невозможна из-за конфликта
  // типа onChange (SimpleCheckboxChangeEvent vs ChangeEvent) и лишних не-HTML атрибутов.
  // Runtime-эквивалентно исходному `useField(props)`: formik читает из конфига только
  // name/type/value/onBlur/onChange/multiple, а type/value/onBlur/onChange здесь не заданы
  // (как и в исходнике — type='checkbox' выставляется на <FormCheck.Input> ниже).
  // Дженерик не указываем: FieldValue по умолчанию `any`, и field.value:any спокойно
  // спредится на <FormCheck.Input> (иначе boolean|null конфликтовал бы с input.value).
  // Точка использования — currentValue: useMemo<boolean | null> — типизирована отдельно.
  const [field, meta] = useField({ name: props.name });
  // FormContext создаётся в utils.js (createContext() без дженерика); при allowJs:false
  // useContext возвращает unknown. Приводим к ожидаемой форме контекста формы
  // (Provider всегда подаёт значение; default — undefined, деструктуризация безопасна).
  const { dirty, submitCount, setFieldValue, validationSchema } = useContext(FormContext) as {
    dirty?: boolean;
    submitCount?: number;
    setFieldValue?: any;
    validationSchema?: any;
  };

  const {
    label,
    checked,
    disabled,
    readOnly,
    required,
    className,
    rootClassName,
    onValidateFail,
    onChange,
    noMarginBottom,
    labelClassName,
    // FIXME: validationSchema из props игнорируется — см. JSDoc в CheckboxInputOwnProps.
    // Деструктурируем, чтобы пропс не утёк на DOM-инпут через {...restProps}.
    validationSchema: _propsValidationSchema,
    mode = 'binary',
    triStateLabels = { yes: 'Да', no: 'Нет', undefined: 'Не определено' },
    hoverable,
    isNextField,
    ...restProps
  } = props;

  const internalRef = useRef<HTMLInputElement>(null);
  // ForwardedRef включает ветку RefCallback (без .current), а ниже мы читаем .current
  // для indeterminate — поэтому приводим к object-ref. Callback-ref никто не передаёт
  // (прямых ref-передач в потребителях нет — см. CHECKBOX_TSX_PLAN.md, риск 2).
  // Runtime-безопасно: даже при callback-ref проверка `if (!el)` ниже просто пропустит запись.
  const combinedRef = (ref || internalRef) as React.RefObject<HTMLInputElement>;

  // Связываем input ↔ label через id. Без него FormCheck.Label рендерит пустой htmlFor
  // (Formik-поле не содержит id), и клик по тексту чекбокса не переключает значение.
  // react-bootstrap прокидывает id ПРОПСА FormCheck в FormContext.controlId, откуда его
  // берут и input, и label. Поэтому id нужно передавать именно на FormCheck (а не на Input).
  const autoId = useId();
  const inputId = restProps.id || autoId;

  const isRequired = required || isRequiredField(validationSchema, field.name);
  const hasError = !isEmpty(meta.error)
    && ((meta.touched && (field.value !== undefined)) || (dirty && (submitCount > 0)));

  const currentValue = useMemo<boolean | null>(() => {
    if (mode === 'binary') return !!field.value;
    if (field.value === null || field.value === undefined) return null;
    return field.value === true ? true : false;
  }, [field.value, mode]);

  const getNextValue = (current: boolean | null): boolean | null => {
    if (mode === 'binary') return !current;
    if (current === null || current === undefined) return true;
    if (current === true) return false;
    return null;
  };

  useEffect(() => {
    if (checked !== undefined) {
      const valueToSet = mode === 'binary'
        ? !!checked
        : (checked === null ? null : !!checked);
      setFieldValue(field.name, valueToSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, mode]);

  useEffect(() => {
    const el = combinedRef?.current;
    if (!el) return;
    el.indeterminate = mode === 'triState' && currentValue === null;
  }, [currentValue, mode, combinedRef]);

  useEffect(() => {
    if (hasError && typeof onValidateFail === 'function') {
      onValidateFail(meta.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const displayLabel = useMemo<React.ReactNode>(() => {
    if (!label) return label;
    if (mode !== 'triState') return label;

    if (currentValue === true) return `${ label }: ${ triStateLabels.yes }`;
    if (currentValue === false) return `${ label }: ${ triStateLabels.no }`;
    return `${ label }: ${ triStateLabels.undefined }`;
  }, [label, mode, currentValue, triStateLabels]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = getNextValue(currentValue);
    setFieldValue(field.name, newValue);

    if (typeof onChange === 'function') {
      // Создаем синтетическое событие, чтобы родительские компоненты могли читать
      // event.target.checked и event.target.value как обычно.
      const syntheticEvent: SimpleCheckboxChangeEvent = {
        ...event,
        target: {
          ...event.target,
          name: field.name,
          value: newValue,
          checked: newValue === true,
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <FormGroup className={ cx(noMarginBottom && 'no-margin-bottom', rootClassName, hasError && 'field-has-error', isNextField && !hasError && !disabled && 'field-is-next') }>
      <FormCheck
        className={ cx('core-checkbox', hoverable && 'hoverable') }
        id={ inputId }
      >
        <FormCheck.Input
          { ...field }
          { ...restProps }
          ref={ combinedRef }
          type='checkbox'
          checked={ currentValue === true }
          disabled={ disabled }
          readOnly={ readOnly }
          className={ cx(className, { required: isRequired }) }
          aria-checked={ mode === 'triState' ? (currentValue === null ? 'mixed' : currentValue) : undefined }
          aria-invalid={ hasError || undefined }
          aria-required={ isRequired || undefined }
          onChange={ handleChange }
        />
        <FormCheck.Label className={ labelClassName }>
          { displayLabel }
        </FormCheck.Label>
      </FormCheck>
    </FormGroup>
  );
});

CheckboxInput.displayName = 'CheckboxInput';

export default CheckboxInput;
